import { PoistaIsometri } from "./IsometriTuonti";
import { useCallback, useEffect, useRef, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { api, ApiError, type Isometri, type Nasta } from "./api";
import { useKirjaustunniste, TOISTON_ILMOITUS } from "./kirjaustunniste";
import Liitteet, { KameraKuvake, lueBase64 } from "./Liitteet";
import { naytaPvm } from "./pvm";
import { t, virheTeksti, type KaannosAvain } from "./kieli";

/* Päätös 85 vaihe 1 (Toni 6.10.2026 «Aloitetaan vaiheesta 1»): virheiden merkintä isometriin linjatarkastuksessa.
   Isometrin sivu (tai linjan valokuva) on taustana; zoom ja siirto sormilla, rullalla tai napeilla. Napautus = luonnosnasta,
   ja lomake tekee siitä numeroidun nastan = punchin (kuvaus, estoluokka, vastuu; kuva liitetään punchille).
   Nasta on sidottu linjaan, lehteen ja revisioon; uudessa revisiossa vanhan revision nastat näkyvät merkinnällä
   «edellinen revisio». Koordinaatit ovat sivun suhteellisia (0…1), joten zoom ei muuta niitä. */

/** kierto (253, T639): valokuvan näyttökierto 0/90/180/270 myötäpäivään. Nastat ovat alkuperäisen kuvan kehyksessä. */
type Pohja = { laji: "isometri"; iso: Isometri } | { laji: "kuva"; id: number; nimi: string; kierto: number };
// Ehdotukset käyttäjän kielellä: kenttä on vapaata tekstiä, joten ehdotus tallentuu sillä kielellä jolla se valittiin.
const VASTUU_EHDOTUKSET: KaannosAvain[] = ["isom.v_urakoitsija", "isom.v_tilaaja", "isom.v_suunnittelija", "isom.v_materiaali", "isom.v_eristys", "isom.v_sahko"];

export default function IsometriMerkinnat({ linjaId, saaKirjata, onMuutos }: { linjaId: number; saaKirjata: boolean; onMuutos?: () => void }) {
  const [isometrit, setIsometrit] = useState<Isometri[] | null>(null);
  const [kuvat, setKuvat] = useState<{ id: number; alkuperainen: string; lisatty: string; kierto?: number }[]>([]);
  const [nastat, setNastat] = useState<Nasta[]>([]);
  const [pohja, setPohja] = useState<Pohja | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [historia, setHistoria] = useState(false);

  const uusi = useViimeisin();   // päätös 99: nasta tallentuu näytetyn isometrin pohjalle
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try {
      const [r, n] = await Promise.all([api.linjanIsometrit(linjaId), api.nastat(linjaId)]);
      if (!tuore()) return;
      setIsometrit(r.isometrit); setKuvat(r.kuvat); setNastat(n); setVirhe(null);
      setPohja((p) => {
        if (p?.laji === "isometri") { const u = r.isometrit.find((i) => i.id === p.iso.id); if (u) return { laji: "isometri", iso: u }; }
        if (p?.laji === "kuva") { const u = r.kuvat.find((k) => k.id === p.id); if (u) return { ...p, kierto: u.kierto ?? 0 }; }
        const eka = r.isometrit.find((i) => !i.korvattu_id);
        return eka ? { laji: "isometri", iso: eka } : null;
      });
    } catch (e) { if (!tuore()) return; setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("isom.lataus_virhe")); setIsometrit([]); }
  }, [linjaId, uusi]);
  useEffect(() => { void lataa(); }, [lataa]);

  if (isometrit === null) return <p className="dim">{t("isom.ladataan")}</p>;
  const voimassa = isometrit.filter((i) => !i.korvattu_id);
  const vanhat = isometrit.filter((i) => i.korvattu_id);

  // Pohjan nastat: tämän isometrin omat + saman lehden aiempien revisioiden nastat (merkinnällä). Kuvalla vain sen omat.
  const naytettavat = !pohja ? [] : pohja.laji === "kuva"
    ? nastat.filter((n) => n.pohja === "kuva" && n.kuva_liite_id === pohja.id)
    : nastat.filter((n) => n.pohja === "isometri" && (n.isometri_id === pohja.iso.id
        || (n.edellinen_revisio && n.lehti_nro === pohja.iso.lehti_nro && !pohja.iso.korvattu_id)));

  return (
    <div className="isomerkinta" data-isometri-nakyma={linjaId}>
      <h3 style={{ margin: "0 0 6px" }}>{t("isom.otsikko")}</h3>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {voimassa.length === 0 && kuvat.length === 0 && (
        <p className="dim" data-isometri-tyhja="">
          {t("isom.tyhja")}
        </p>
      )}
      <div className="isomerkinta__pohjat" role="tablist" aria-label={t("isom.pohja_aria")}>
        {voimassa.map((i) => (
          <button key={i.id} role="tab" aria-selected={pohja?.laji === "isometri" && pohja.iso.id === i.id}
            className={`nappi nappi--kevyt${pohja?.laji === "isometri" && pohja.iso.id === i.id ? " nappi--valittu" : ""}`}
            data-isometri-valinta={i.id} onClick={() => setPohja({ laji: "isometri", iso: i })}>
            {i.muoto === "vertex_kirja" ? `${i.piirustusnumero ?? t("isom.piirustus")}` : <>{t("isom.lehti")} {i.lehti_nro ?? "?"}{i.lehti_kpl ? `/${i.lehti_kpl}` : ""}</>} · rev {i.revisio ?? "–"}
            {i.nastoja > 0 ? ` · ${t("isom.n_merk", { n: i.nastoja })}` : ""}
          </button>
        ))}
        {kuvat.map((k) => (
          <button key={`k${k.id}`} role="tab" aria-selected={pohja?.laji === "kuva" && pohja.id === k.id}
            className={`nappi nappi--kevyt${pohja?.laji === "kuva" && pohja.id === k.id ? " nappi--valittu" : ""}`}
            data-kuva-valinta={k.id} onClick={() => setPohja({ laji: "kuva", id: k.id, nimi: k.alkuperainen, kierto: k.kierto ?? 0 })}>
            {t("isom.kuva")} · {k.alkuperainen.length > 24 ? k.alkuperainen.slice(0, 22) + "…" : k.alkuperainen}
          </button>
        ))}
        {vanhat.length > 0 && (
          <button className="nappi nappi--kevyt" onClick={() => setHistoria((h) => !h)} aria-expanded={historia}>
            {t("isom.aiemmat", { n: vanhat.length })}
          </button>
        )}
      </div>
      {historia && (
        <ul className="isomerkinta__historia">
          {vanhat.map((i) => (
            <li key={i.id}>
              <button className="tekstilinkki" data-isometri-valinta={i.id} onClick={() => setPohja({ laji: "isometri", iso: i })}>
                {t("isom.lehti")} {i.lehti_nro ?? "?"} · rev {i.revisio ?? "–"} · {t("isom.korvattu")} · {i.tiedostonimi}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 201: valitun isometrin poisto — sama nappi kuin kohdistamattomien listassa. */}
      {saaKirjata && pohja?.laji === "isometri" && !pohja.iso.korvattu_id && (
        <PoistaIsometri iso={pohja.iso} valmis={() => { setPohja(null); void lataa(); onMuutos?.(); }} />
      )}
      {/* 199: kirjan kokoonpanossa merkinnät tehdään yleiskuvaan (ensimmäinen sivu); detaljisivut avautuvat kuvina. */}
      {pohja?.laji === "isometri" && pohja.iso.sivu_alku && pohja.iso.sivu_loppu && pohja.iso.sivu_loppu > pohja.iso.sivu_alku && (
        <p className="dim" data-isometri-sivut={pohja.iso.id} style={{ margin: "4px 0" }}>
          {t("isom.yleiskuva", { n: pohja.iso.sivu_alku })}{" "}
          {Array.from({ length: pohja.iso.sivu_loppu - pohja.iso.sivu_alku }, (_, j) => pohja.iso.sivu_alku! + 1 + j).map((n) => (
            <a key={n} href={api.isometrinSivu(pohja.iso.id, 2400, n)} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{t("isom.sivu", { n })}</a>
          ))}
        </p>
      )}
      {pohja && (
        <Merkinta key={pohja.laji === "isometri" ? `i${pohja.iso.id}` : `k${pohja.id}`} linjaId={linjaId} pohja={pohja}
          nastat={naytettavat} saaKirjata={saaKirjata && !(pohja.laji === "isometri" && pohja.iso.korvattu_id)}
          paivita={async () => { await lataa(); onMuutos?.(); }} />
      )}

      {saaKirjata && (
        <div style={{ marginTop: 10 }}>
          {/* Toni 19.10.2026 klo 19.45: «Lisää valokuva» päätoimintonappina kamerakuvakkeella (ennen haalea «Lisää liite»). */}
          <Liitteet kohdeTaulu="linjakuva" kohdeId={linjaId} saaMuokata={saaKirjata} saaPoistaa={false} kamera paatoiminto
            lisaaTeksti={t("isom.lisaa_valokuva")} otsikko={t("isom.valokuvat")} muuttui={() => void lataa()} />
        </div>
      )}
    </div>
  );
}

function Merkinta({ linjaId, pohja, nastat, saaKirjata, paivita }: {
  linjaId: number; pohja: Pohja; nastat: Nasta[]; saaKirjata: boolean; paivita: () => Promise<void>;
}) {
  const kehys = useRef<HTMLDivElement>(null);
  const sivuRef = useRef<HTMLDivElement>(null);
  // Sivun laatikko = kuva sovitettuna kehykseen kuvasuhteessaan. Nastat ja napautus mitataan tätä vasten, ei kehystä
  // (object-fit: contain jättäisi reunat, ja koordinaatit osoittaisivat kehykseen eikä piirustukseen).
  const [luonnollinen, setLuonnollinen] = useState<{ w: number; h: number } | null>(null);
  const [kehysKoko, setKehysKoko] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = kehys.current; if (!el) return;
    const ro = new ResizeObserver(() => setKehysKoko({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el); setKehysKoko({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  /* T639 (Toni 20.10.2026 klo 16.36): valokuvan kierto 90° kerrallaan. Sivun laatikko on käännetyn kuvan kokoinen; sen sisällä kuvataso on
     alkuperäisen kuvan kokoinen ja käännetty keskipisteensä ympäri, ja nastat ovat kuvatason sisällä alkuperäisen kuvan koordinaateissa —
     nasta kääntyy kuvan mukana ja pysyy samassa kohdassa kuvaa. Napautus muunnetaan näytön koordinaateista takaisin alkuperäiseen kehykseen. */
  const kierto = pohja.laji === "kuva" ? pohja.kierto : 0;
  const pysty = kierto === 90 || kierto === 270;
  const nakyva = luonnollinen ? (pysty ? { w: luonnollinen.h, h: luonnollinen.w } : luonnollinen) : null;
  const sk = nakyva && kehysKoko.w ? Math.min(kehysKoko.w / nakyva.w, kehysKoko.h / nakyva.h) : 0;
  const sivu = nakyva && sk ? { width: nakyva.w * sk, height: nakyva.h * sk,
    left: (kehysKoko.w - nakyva.w * sk) / 2, top: (kehysKoko.h - nakyva.h * sk) / 2 } : null;
  const kuvataso: React.CSSProperties = luonnollinen && sk
    ? { position: "absolute", left: "50%", top: "50%", width: luonnollinen.w * sk, height: luonnollinen.h * sk,
        transform: `translate(-50%, -50%) rotate(${kierto}deg)` }
    : { position: "absolute", inset: 0 };
  /** Näytön suhteellinen piste (X, Y) käännetyssä laatikossa → alkuperäisen kuvan suhteellinen piste. */
  const alkuperaiseen = (X: number, Y: number) => kierto === 90 ? { x: Y, y: 1 - X } : kierto === 180 ? { x: 1 - X, y: 1 - Y }
    : kierto === 270 ? { x: 1 - Y, y: X } : { x: X, y: Y };
  const [kiertaa, setKiertaa] = useState(false);
  async function kierra() {
    if (pohja.laji !== "kuva") return;
    setKiertaa(true); setViesti(null);
    try { await api.kierraLiite(pohja.id, (kierto + 90) % 360); await paivita(); }
    catch (e) { setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("isom.tallennus_virhe")); }
    finally { setKiertaa(false); }
  }
  const [nakyma, setNakyma] = useState({ s: 1, x: 0, y: 0 });
  const osoittimet = useRef(new Map<number, { x: number; y: number }>());
  const ele = useRef<{ alku: { x: number; y: number }; aika: number; liikkui: boolean; nipistys?: { etaisyys: number; s: number } } | null>(null);
  const [luonnos, setLuonnos] = useState<{ x: number; y: number } | null>(null);
  const [valittu, setValittu] = useState<number | null>(null);
  const [vika, setVika] = useState(""); const [luokka, setLuokka] = useState<"" | "A" | "B" | "C">("");
  const [vastuu, setVastuu] = useState(""); const [huomiot, setHuomiot] = useState("");
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();
  const [viesti, setViesti] = useState<string | null>(null);
  const [tallentaa, setTallentaa] = useState(false);
  const [juuriLuotu, setJuuriLuotu] = useState<{ numero: number; punch_id: number } | null>(null);
  const [ladattu, setLadattu] = useState(false);

  const src = pohja.laji === "isometri" ? api.isometrinSivu(pohja.iso.id, 2400) : api.liiteOsoite(pohja.id);

  const sovita = useCallback(() => setNakyma({ s: 1, x: 0, y: 0 }), []);
  const zoomaa = (kerroin: number, cx?: number, cy?: number) => setNakyma((v) => {
    const r = kehys.current!.getBoundingClientRect();
    const px = (cx ?? r.left + r.width / 2) - r.left; const py = (cy ?? r.top + r.height / 2) - r.top;
    const s = Math.min(8, Math.max(1, v.s * kerroin)); const k = s / v.s;
    return s === 1 ? { s: 1, x: 0, y: 0 } : { s, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
  });

  function alas(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    osoittimet.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = [...osoittimet.current.values()];
    if (p.length === 1) ele.current = { alku: { x: e.clientX, y: e.clientY }, aika: Date.now(), liikkui: false };
    else if (p.length === 2 && ele.current) {
      ele.current.liikkui = true;
      ele.current.nipistys = { etaisyys: Math.hypot(p[0]!.x - p[1]!.x, p[0]!.y - p[1]!.y), s: nakyma.s };
    }
  }
  function liiku(e: React.PointerEvent) {
    const ed = osoittimet.current.get(e.pointerId); if (!ed || !ele.current) return;
    osoittimet.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = [...osoittimet.current.values()];
    if (p.length === 2 && ele.current.nipistys) {
      const d = Math.hypot(p[0]!.x - p[1]!.x, p[0]!.y - p[1]!.y);
      const tavoite = ele.current.nipistys.s * d / ele.current.nipistys.etaisyys;
      zoomaa(tavoite / nakyma.s, (p[0]!.x + p[1]!.x) / 2, (p[0]!.y + p[1]!.y) / 2);
      return;
    }
    if (Math.hypot(e.clientX - ele.current.alku.x, e.clientY - ele.current.alku.y) > 6) ele.current.liikkui = true;
    if (ele.current.liikkui && nakyma.s > 1) setNakyma((v) => ({ ...v, x: v.x + e.clientX - ed.x, y: v.y + e.clientY - ed.y }));
  }
  function ylos(e: React.PointerEvent) {
    osoittimet.current.delete(e.pointerId);
    const g = ele.current;
    if (osoittimet.current.size === 0) ele.current = null;
    if (!g || g.liikkui || Date.now() - g.aika > 600 || !saaKirjata || !sivuRef.current) return;
    // Napautus: sijainti sivun suhteellisina koordinaatteina (zoom ja siirto eivät vaikuta).
    const r = sivuRef.current.getBoundingClientRect();
    const X = (e.clientX - r.left) / r.width; const Y = (e.clientY - r.top) / r.height;
    if (X < 0 || X > 1 || Y < 0 || Y > 1) return;
    const { x, y } = alkuperaiseen(X, Y);
    setLuonnos({ x: Math.round(x * 10000) / 10000, y: Math.round(y * 10000) / 10000 }); setValittu(null); setJuuriLuotu(null); setViesti(null);
  }

  /* T640 (Toni 20.10.2026 klo 16.38): kuvat jo «Uusi merkintä» -lomakkeelle. Merkintä on linjan punch (nasta viittaa siihen), ja punchin
     kuvat ovat liitteitä (kohde_taulu punch) kuten Selailun punch-listassa. Kuvat pidetään muistissa tallennukseen asti: peruttu merkintä ei
     jätä irrallisia kuvia. Kamera avautuu yhdellä painalluksella, galleria omana nappinaan (sama kuin linjatarkastuksen kuvissa). */
  const [kuvaLuonnokset, setKuvaLuonnokset] = useState<{ nimi: string; sisalto: string }[]>([]);
  const kamerasyote = useRef<HTMLInputElement>(null);
  const galleriasyote = useRef<HTMLInputElement>(null);
  async function lisaaKuvat(tiedostot: FileList | null) {
    if (!tiedostot?.length) return;
    try {
      const uudet = await Promise.all([...tiedostot].map(async (f) => ({ nimi: f.name || "kuva.jpg", sisalto: await lueBase64(f) })));
      setKuvaLuonnokset((v) => [...v, ...uudet]);
    } catch (e) { setViesti(e instanceof Error ? e.message : t("isom.tallennus_virhe")); }
    if (kamerasyote.current) kamerasyote.current.value = "";
    if (galleriasyote.current) galleriasyote.current.value = "";
  }

  async function tallenna() {
    if (!luonnos) return;
    if (!vika.trim()) { setViesti(t("isom.kuvaa_vika")); return; }
    if (!luokka) { setViesti(t("pc.valitse_luokka_virhe")); return; }
    setTallentaa(true); setViesti(null);
    try {
      const r = await api.lisaaNasta(linjaId, {
        pohja: pohja.laji, ...(pohja.laji === "isometri" ? { isometri_id: pohja.iso.id } : { kuva_liite_id: pohja.id }),
        x: luonnos.x, y: luonnos.y, vika: vika.trim(), luokka,
        ...(vastuu.trim() && { vastuu: vastuu.trim() }), ...(huomiot.trim() && { huomiot: huomiot.trim() }),
        kirjaustunniste: tunniste,
      });
      vaihdaTunniste();
      if (r.toisto) setViesti(TOISTON_ILMOITUS);
      // T640: lomakkeella valitut kuvat merkinnän punchin liitteiksi vasta kun punch on olemassa (liite tarvitsee kohteen). Toistossa
      // (sama kirjaustunniste) kuvat on jo lähetetty ensimmäisellä kerralla.
      let kuviaVirhe = 0;
      if (!r.toisto) for (const k of kuvaLuonnokset) {
        try { await api.lisaaLiite({ kohde_taulu: "punch", kohde_id: r.punch_id, nimi: k.nimi, sisalto: k.sisalto }); }
        catch { kuviaVirhe++; }
      }
      if (kuviaVirhe) setViesti(t("isom.kuvat_virhe", { n: kuviaVirhe }));
      setJuuriLuotu({ numero: r.numero, punch_id: r.punch_id });
      setLuonnos(null); setVika(""); setLuokka(""); setVastuu(""); setHuomiot(""); setKuvaLuonnokset([]);
      await paivita();
    } catch (e) { setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("isom.tallennus_virhe")); }
    finally { setTallentaa(false); }
  }

  const avoimia = nastat.filter((n) => n.avoin && !n.edellinen_revisio).length;
  const nayta = nastat.find((n) => n.numero === valittu) ?? null;

  return (
    <div className="isomerkinta__tyopoyta">
      <div className="isomerkinta__tila dim">
        {pohja.laji === "isometri"
          ? <>{t("isom.lehti")} {pohja.iso.lehti_nro ?? "?"}{pohja.iso.lehti_kpl ? `/${pohja.iso.lehti_kpl}` : ""} · {t("isom.revisio")} {pohja.iso.revisio ?? "–"}
              {pohja.iso.piirustusnumero ? ` · ${pohja.iso.piirustusnumero}` : ""}{pohja.iso.korvattu_id ? ` · ${t("isom.korvattu_uudemmalla")}` : ""}</>
          : <>{t("isom.valokuva")} · {pohja.nimi}</>}
        {" · "}{t("isom.avoimia", { n: avoimia })}
        {saaKirjata && <> · <b>{t("isom.napauta")}</b> {t("isom.merkitaksesi")}</>}
      </div>
      <div className="isomerkinta__napit">
        <button className="nappi nappi--kevyt" onClick={() => zoomaa(1.5)} aria-label={t("isom.lahenna")}>+</button>
        <button className="nappi nappi--kevyt" onClick={() => zoomaa(1 / 1.5)} aria-label={t("isom.loitonna")}>−</button>
        <button className="nappi nappi--kevyt" onClick={sovita}>{t("isom.sovita")}</button>
        {pohja.laji === "kuva" && saaKirjata && (
          <button className="nappi nappi--kevyt" data-kuva-kierra={kierto} disabled={kiertaa} onClick={() => void kierra()}
            aria-label={t("isom.kierra_aria")}>↻ {t("isom.kierra")}</button>
        )}
      </div>
      <div ref={kehys} className="isomerkinta__kehys" data-merkinta-kehys=""
        onPointerDown={alas} onPointerMove={liiku} onPointerUp={ylos} onPointerCancel={(e) => { osoittimet.current.delete(e.pointerId); ele.current = null; }}
        onWheel={(e) => { e.preventDefault(); zoomaa(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX, e.clientY); }}>
        <div className="isomerkinta__taso" style={{ transform: `translate(${nakyma.x}px, ${nakyma.y}px) scale(${nakyma.s})` }}>
          <div ref={sivuRef} className="isomerkinta__sivu" data-merkinta-sivu="" data-kierto={kierto} style={sivu ?? { inset: 0 }}>
          <div data-merkinta-kuvataso="" style={kuvataso}>
          <img src={src} alt={pohja.laji === "isometri" ? t("isom.isometrin_sivu") : t("isom.linjan_valokuva")} draggable={false}
            data-merkinta-kuva="" data-ladattu={ladattu ? "1" : "0"}
            onLoad={(e) => { setLuonnollinen({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight }); setLadattu(true); }} />
          {nastat.map((n) => (
            <button key={n.id} type="button" data-nasta={n.numero} data-nasta-avoin={n.avoin ? "1" : "0"}
              data-nasta-edellinen={n.edellinen_revisio ? "1" : "0"}
              className={`nasta${n.avoin ? " nasta--avoin" : " nasta--korjattu"}${n.edellinen_revisio ? " nasta--edellinen" : ""}${valittu === n.numero ? " nasta--valittu" : ""}`}
              style={{ left: `${n.x * 100}%`, top: `${n.y * 100}%`, transform: `translate(-50%, -50%) rotate(${-kierto}deg) scale(${1 / nakyma.s})` }}
              title={`#${n.numero} ${n.vika}${n.edellinen_revisio ? ` (${t("isom.edellinen_revisio")} ${n.revisio ?? "–"})` : ""}`}
              onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}
              onClick={() => { setValittu(n.numero); setLuonnos(null); }}>
              {n.avoin ? n.numero : "✓"}
            </button>
          ))}
          {luonnos && (
            <span className="nasta nasta--luonnos" data-nasta-luonnos=""
              style={{ left: `${luonnos.x * 100}%`, top: `${luonnos.y * 100}%`, transform: `translate(-50%, -50%) rotate(${-kierto}deg) scale(${1 / nakyma.s})` }}>+</span>
          )}
          </div>
          </div>
        </div>
      </div>

      {luonnos && saaKirjata && (
        <fieldset className="isomerkinta__lomake" data-nasta-lomake="">
          <legend>{t("isom.uusi")}</legend>
          <label className="dim">{t("psel.vika")}<br />
            <textarea value={vika} rows={2} data-kentta="vika" onChange={(e) => setVika(e.target.value)} style={{ width: "100%" }} /></label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="dim">{t("isom.estoluokka")}<br />
              <select value={luokka} data-kentta="luokka" onChange={(e) => setLuokka(e.target.value as "" | "A" | "B" | "C")}>
                <option value="">{t("pc.valitse_luokka")}</option><option value="A">A · {t("pc.luokka.A")}</option>
                <option value="B">B · {t("pc.luokka.B")}</option><option value="C">C · {t("pc.luokka.C")}</option>
              </select></label>
            <label className="dim" style={{ flex: 1, minWidth: 150 }}>{t("isom.vastuu")}<br />
              <input value={vastuu} list="nasta-vastuu" data-kentta="vastuu" onChange={(e) => setVastuu(e.target.value)} style={{ width: "100%" }} />
              <datalist id="nasta-vastuu">{VASTUU_EHDOTUKSET.map((v) => <option key={v} value={t(v)} />)}</datalist></label>
          </div>
          <label className="dim">{t("ltar.huomiot")}<br />
            <input value={huomiot} onChange={(e) => setHuomiot(e.target.value)} style={{ width: "100%" }} /></label>
          <div className="dim" style={{ marginTop: 6 }}>{t("isom.kuva_viasta")}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }} data-nasta-kuvat={kuvaLuonnokset.length}>
            <button type="button" className="nappi nappi--kuvake" disabled={tallentaa} data-nasta-kamera="" onClick={() => kamerasyote.current?.click()}>
              <KameraKuvake />{t("isom.lisaa_kuva")}</button>
            <input hidden ref={kamerasyote} type="file" accept="image/*" capture="environment" data-nasta-kamera-syote=""
              onChange={(e) => void lisaaKuvat(e.target.files)} />
            <button type="button" className="nappi nappi--kevyt" disabled={tallentaa} onClick={() => galleriasyote.current?.click()}>{t("liite.galleriasta")}</button>
            <input hidden ref={galleriasyote} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic" data-nasta-galleria-syote=""
              onChange={(e) => void lisaaKuvat(e.target.files)} />
            {kuvaLuonnokset.map((k, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <img src={k.sisalto} alt={k.nimi} style={{ width: 48, height: 48, objectFit: "cover" }} />
                <button type="button" className="tekstilinkki" aria-label={t("isom.poista_kuva", { nimi: k.nimi })}
                  onClick={() => setKuvaLuonnokset((v) => v.filter((_, j) => j !== i))}>✕</button>
              </span>
            ))}
          </div>
          {viesti && <p className="huom huom--vika" role="alert" data-nasta-virhe="">{viesti}</p>}
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button className="nappi nappi--paa" disabled={tallentaa} data-nasta-tallenna="" onClick={() => void tallenna()}>{t("isom.tallenna")}</button>
            <button className="nappi nappi--kevyt" onClick={() => { setLuonnos(null); setViesti(null); setKuvaLuonnokset([]); }}>{t("isom.peru")}</button>
          </div>
        </fieldset>
      )}

      {juuriLuotu && (
        <div className="isomerkinta__luotu" data-nasta-luotu={juuriLuotu.numero}>
          <p style={{ margin: "6px 0" }}><b>{t("isom.tallennettu", { n: juuriLuotu.numero })}</b> {t("isom.tallennettu_jatko")}</p>
          <Liitteet pienoiskuvat kohdeTaulu="punch" kohdeId={juuriLuotu.punch_id} saaMuokata={saaKirjata} saaPoistaa={false} otsikko={t("isom.kuva_viasta")}
            muuttui={() => void paivita()} />
          {viesti && <p className="dim">{viesti}</p>}
        </div>
      )}

      {nayta && (
        <div className="isomerkinta__valittu" data-nasta-tiedot={nayta.numero}>
          <p style={{ margin: "6px 0" }}>
            <b>#{nayta.numero}</b> {nayta.vika} · {t("isom.luokka_pieni")} {nayta.luokka ?? "–"}{nayta.vastuu ? ` · ${t("isom.vastuu_pieni")} ${nayta.vastuu}` : ""}
            {" · "}{nayta.avoin ? t("psel.avoin") : `${t("psel.korjattu")} ${nayta.kuittaus_pvm ? naytaPvm(nayta.kuittaus_pvm) : ""}`}
            {nayta.edellinen_revisio ? ` · ${t("isom.edellinen_revisio")} ${nayta.revisio ?? "–"}` : ""}
          </p>
          <p className="dim" style={{ margin: 0 }}>{t("isom.merkitsi")} {nayta.huomioinut ?? "–"}{nayta.huomio_pvm ? ` ${naytaPvm(nayta.huomio_pvm)}` : ""}.
            {" "}{t("isom.kuittaus_listasta")}</p>
          <Liitteet pienoiskuvat kohdeTaulu="punch" kohdeId={nayta.punch_id} saaMuokata={saaKirjata} saaPoistaa={false} otsikko={t("pu.kuvat")} />
        </div>
      )}

      {nastat.length > 0 && (
        <ol className="isomerkinta__lista" data-nasta-lista="">
          {nastat.map((n) => (
            <li key={n.id} value={n.numero}>
              <button className="tekstilinkki" onClick={() => setValittu(n.numero)}>
                #{n.numero} · {n.vika} · {n.luokka ?? "–"}{n.vastuu ? ` · ${n.vastuu}` : ""} · {n.avoin ? t("psel.avoin") : t("psel.korjattu")}
                {n.edellinen_revisio ? ` · edellinen revisio ${n.revisio ?? "–"}` : ""}
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
